/**
 * How a value from the API is written for a person holding a phone.
 *
 * Nothing here decides anything and nothing here invents anything. Every function takes what the
 * API returned, including the nulls, and returns a string or null. A screen that has to reason
 * about a missing value is a screen that eventually fills one in with a guess.
 *
 * TWO KINDS OF TIME, KEPT APART. A DAY is a `YYYY-MM-DD` string with no zone, and it is taken
 * apart by hand: `new Date('2026-09-10')` is midnight UTC, which is the evening before in Florida,
 * and a schedule that is off by one is a cleaner at the wrong house. An INSTANT is an ISO UTC
 * string, and it is rendered through Intl in America/New_York, never through a Date getter, so the
 * window reads the same on a phone that happens to be set to another zone.
 */
import { formatCents, type SavedMethodType } from '@fk/shared';

/** The only shape the API accepts, copied from the same regex the route validates with. */
export const E164 = /^\+[1-9]\d{7,14}$/;

/** Every home served is in Florida. The schema keeps no state column, so it is a constant here. */
export const STATE = 'FL';

/** The one zone the business runs in. Same constant as `api/src/lib/clock.ts`. */
export const BUSINESS_TIME_ZONE = 'America/New_York';

/** E.164 to the shape a person reads back down a phone: +17273377775 becomes (727) 337-7775. */
export function formatPhone(e164: string | null): string | null {
  if (!e164) return null;
  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (!match) return e164;
  const [, area, exchange, line] = match;
  return `(${area}) ${exchange}-${line}`;
}

/**
 * What somebody types, turned into what the API accepts.
 *
 * The API refuses anything that is not E.164, and it is right to: the phone system knows a caller
 * by exactly that string, so a number stored as (727) 337-7775 makes an inbound call from a client
 * of a year arrive as an unknown number. Ten digits are read as North American, which is the whole
 * service area. Anything else is handed back untouched so the person sees their own typing in the
 * error rather than something this function guessed at.
 */
export function toE164(typed: string): string | null {
  const trimmed = typed.trim();
  if (!trimmed) return null;
  const digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return trimmed;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * The same time of day the API accepts, character for character with `TIME_OF_DAY` in
 * `api/src/schedule/dates.ts`. Hours stop at 23: a looser pattern here would let the screen enable
 * Save on a window the server then refuses, and would print "24:00" as noon.
 */
const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const MS_PER_DAY = 86_400_000;

/** A day taken apart by hand, or null when the string is not one. */
function dayParts(day: string): [number, number, number] | null {
  const match = DAY.exec(day);
  if (!match) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function toUtcMidnight(day: string): number | null {
  const parts = dayParts(day);
  if (!parts) return null;
  const [year, month, date] = parts;
  return Date.UTC(year, month - 1, date);
}

function fromUtcMidnight(ms: number): string {
  const at = new Date(ms);
  const year = String(at.getUTCFullYear()).padStart(4, '0');
  const month = String(at.getUTCMonth() + 1).padStart(2, '0');
  const date = String(at.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${date}`;
}

/** True for a real calendar day. Rejects 2026-02-30: a round trip that changes the string was never a day. */
export function isDay(value: string): boolean {
  const ms = toUtcMidnight(value);
  return ms !== null && fromUtcMidnight(ms) === value;
}

/** A day plus or minus some days. Same arithmetic as the server's `schedule/dates.ts`. */
export function addDays(day: string, count: number): string {
  const ms = toUtcMidnight(day);
  if (ms === null) return day;
  return fromUtcMidnight(ms + count * MS_PER_DAY);
}

/** Whole days from `from` to `to`; negative when `to` is earlier; null when either is not a day. */
export function daysBetween(from: string, to: string): number | null {
  const a = toUtcMidnight(from);
  const b = toUtcMidnight(to);
  if (a === null || b === null) return null;
  return Math.round((b - a) / MS_PER_DAY);
}

/**
 * A YYYY-MM-DD calendar date, written out.
 *
 * Parsed by hand rather than through Date, because `new Date('2026-09-03')` is midnight UTC and
 * every phone in this business is four or five hours behind that, so the built-in formatters
 * render the day before. A date that is off by one on a schedule is not a cosmetic bug.
 */
export function formatDay(day: string | null): string | null {
  if (!day) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!match) return day;
  const [, year, month, date] = match;
  const name = MONTHS[Number(month) - 1];
  if (!name) return day;
  return `${name} ${Number(date)}, ${year}`;
}

/**
 * "Oct 21, 2026": a day in the style `formatInstant` writes a moment, without the time, for a list that
 * mixes the two and holds some rows as a day alone (a payment is received on a day, at no hour).
 */
export function mediumDay(day: string): string {
  const parts = dayParts(day);
  if (!parts) return day;
  const [year, month, date] = parts;
  const monthName = MONTHS[month - 1];
  if (!monthName) return day;
  return `${monthName.slice(0, 3)} ${date}, ${year}`;
}

/** "Thursday", for a day. The weekday is a projection of the date and is never stored. */
export function weekdayName(day: string): string {
  const ms = toUtcMidnight(day);
  if (ms === null) return day;
  return WEEKDAYS[new Date(ms).getUTCDay()] ?? day;
}

/** "Thu, Sep 10". Short enough for a heading a thumb scrolls past. */
export function shortDay(day: string): string {
  const parts = dayParts(day);
  if (!parts) return day;
  const [, month, date] = parts;
  const monthName = MONTHS[month - 1];
  if (!monthName) return day;
  return `${weekdayName(day).slice(0, 3)}, ${monthName.slice(0, 3)} ${date}`;
}

/** "Thursday, September 10", the way the client's message says it. */
export function longDay(day: string): string {
  const parts = dayParts(day);
  if (!parts) return day;
  const [, month, date] = parts;
  const monthName = MONTHS[month - 1];
  if (!monthName) return day;
  return `${weekdayName(day)}, ${monthName} ${date}`;
}

/* ------------------------------------------------------------------ *
 * Times of day and arrival windows.
 * ------------------------------------------------------------------ */

/** The window a new series gets unless somebody types another. What the card describes. */
export const DEFAULT_WINDOW_START = '09:00';
export const DEFAULT_WINDOW_END = '13:00';

/** True for the `HH:MM` an `<input type="time">` hands back and the API accepts. */
export function isTimeOfDay(value: string): boolean {
  return HHMM.test(value);
}

/** "9:00 am", from an `HH:MM` time of day. */
export function clockTime(hhmm: string): string {
  const match = HHMM.exec(hhmm);
  if (!match) return hhmm;
  const hours = Number(match[1]);
  const minutes = match[2] ?? '00';
  const suffix = hours < 12 ? 'am' : 'pm';
  const shown = hours % 12 === 0 ? 12 : hours % 12;
  return `${shown}:${minutes} ${suffix}`;
}

/** "9:00 am to 1:00 pm", from the two `HH:MM` values a series stores. */
export function formatLocalWindow(startLocal: string, endLocal: string): string {
  return `${clockTime(startLocal)} to ${clockTime(endLocal)}`;
}

/** "Monday, Wednesday and Friday": names joined as a sentence joins them. */
function listed(names: readonly string[]): string {
  return names.length < 2
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * THE BUSINESS DAY IN HER WORDS (the staging walk of 2026-09-23): "Monday to Saturday, 9:00 am to 6:00
 * pm", from the business day an owner set, which her page reads rather than types, since the automatic
 * charge runs inside it alone. Days as JavaScript numbers them, 0 for Sunday: three or more in one run,
 * over the end of the week too, by the run's ends; fewer, or more than one run, one by one from Monday;
 * all seven as every day.
 */
export function businessHoursWords(hours: {
  readonly start: string;
  readonly end: string;
  readonly days: readonly number[];
}): string {
  const days = [...new Set(hours.days)].filter((day) => day >= 0 && day <= 6);
  const has = (day: number) => days.includes(((day % 7) + 7) % 7);
  const starts = days.filter((day) => !has(day - 1));
  const run = days.length >= 3 && days.length < 7 && starts.length === 1;
  const fromMonday = [...days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
  const which =
    days.length === 7
      ? 'every day'
      : run
        ? `${WEEKDAYS[starts[0]!]} to ${WEEKDAYS[(starts[0]! + days.length - 1) % 7]}`
        : listed(fromMonday.map((day) => WEEKDAYS[day]!));
  return `${which}, ${formatLocalWindow(hours.start, hours.end)}`;
}

const clockInZone = new Intl.DateTimeFormat('en-US', {
  timeZone: BUSINESS_TIME_ZONE,
  hourCycle: 'h23',
  hour: '2-digit',
  minute: '2-digit',
});

/** The `HH:MM` an instant shows on a clock in the business zone, or null when it is not an instant. */
export function timeOfDayOf(instant: string | null): string | null {
  if (!instant) return null;
  const ms = new Date(instant).getTime();
  if (Number.isNaN(ms)) return null;
  let hour = '00';
  let minute = '00';
  for (const part of clockInZone.formatToParts(new Date(ms))) {
    if (part.type === 'hour') hour = String(Number(part.value) % 24).padStart(2, '0');
    if (part.type === 'minute') minute = part.value.padStart(2, '0');
  }
  return `${hour}:${minute}`;
}

/**
 * "9:00 am to 1:00 pm", from the two UTC instants a visit stores, read in America/New_York. Null
 * when the visit has no window, which a screen should say in words rather than leave blank.
 */
export function formatWindow(startIso: string | null, endIso: string | null): string | null {
  const start = timeOfDayOf(startIso);
  const end = timeOfDayOf(endIso);
  if (!start || !end) return null;
  return formatLocalWindow(start, end);
}

const instantInZone = new Intl.DateTimeFormat('en-US', {
  timeZone: BUSINESS_TIME_ZONE,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/** "Sep 10, 2026, 3:42 pm", for when something was recorded or told. */
export function formatInstant(instant: string | null): string | null {
  if (!instant) return null;
  const ms = new Date(instant).getTime();
  if (Number.isNaN(ms)) return instant;
  return instantInZone
    .format(new Date(ms))
    .replace(/\s(AM|PM)$/, (_, m: string) => ` ${m.toLowerCase()}`);
}

const dayInZone = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * The YYYY-MM-DD an instant fell on in the business zone, so "how long has this been waiting" can
 * be answered against the server's `today` and never against the phone's clock. Slicing the first
 * ten characters of the instant would be the UTC day, which for anything recorded after 8pm here
 * is tomorrow, and an enquiry that has waited one day would read as two.
 */
export function dayOf(instant: string | null): string | null {
  if (!instant) return null;
  const ms = new Date(instant).getTime();
  if (Number.isNaN(ms)) return null;
  return dayInZone.format(new Date(ms));
}

/**
 * "Sep 10, 3:42 pm": the same moment `formatInstant` writes, without the year, for a line narrow
 * enough that the longer form would wrap and grow every row it sits in. Nothing decides on the
 * year here; the card, the work panel and every detail list keep the full form, so the short one
 * is a column's width and not a shorter fact. Built from the two zone-aware pieces above rather
 * than from a second Intl format, so one rule about America/New_York lives in this file and not
 * two.
 */
export function shortInstant(instant: string | null): string | null {
  const day = dayOf(instant);
  const time = timeOfDayOf(instant);
  if (!day || !time) return null;
  const parts = dayParts(day);
  if (!parts) return null;
  const [, month, date] = parts;
  const monthName = MONTHS[month - 1];
  if (!monthName) return null;
  return `${monthName.slice(0, 3)} ${date}, ${clockTime(time)}`;
}

/**
 * Rows in the order they came, gathered under their day. The API already sorts by date and window,
 * so this only groups; it never reorders.
 */
export function groupByDay<T extends { readonly scheduledDate: string }>(
  rows: readonly T[],
): readonly (readonly [string, readonly T[]])[] {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const bucket = groups.get(row.scheduledDate);
    if (bucket) bucket.push(row);
    else groups.set(row.scheduledDate, [row]);
  }
  return [...groups.entries()];
}

/** Cents to dollars, through the shared formatter, so one rounding rule exists in one place. */
export const money = (cents: number): string => formatCents(cents);

/**
 * Dollars typed into a box, turned into whole cents, or null when it is not a figure at all.
 * Used for what one clean pays a cleaner, and for the one figure the published card itself says is
 * quoted after a walkthrough. No published price is ever typed anywhere.
 */
export function centsFromDollars(typed: string): number | null {
  const trimmed = typed.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const [whole, fraction = ''] = trimmed.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}

/**
 * Cents to the plain "180.50" a dollar box is prefilled with, the inverse of `centsFromDollars`.
 * No currency sign and no thousands separator, so what the box holds is exactly what it accepts
 * back. Cents are whole, so nothing here is ever approximated.
 */
export function dollarsForInput(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const magnitude = Math.abs(cents);
  return `${sign}${Math.floor(magnitude / 100)}.${String(magnitude % 100).padStart(2, '0')}`;
}

/** A non-negative whole number typed into a box. `undefined` means "typed, but not a number". */
export function wholeNumber(typed: string): number | null | undefined {
  const trimmed = typed.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  return Number(trimmed);
}

/** Trimmed, or null. The API takes null for "not known" and would take '' as a real answer. */
export function orNull(typed: string): string | null {
  const trimmed = typed.trim();
  return trimmed ? trimmed : null;
}

/* ------------------------------------------------------------------ *
 * Durations in words, for a countdown and for how late a promise was.
 * ------------------------------------------------------------------ */

/** How long a revealed code stays on the screen before it is hidden again. */
export const REVEAL_SECONDS = 60;

/** "one minute", "45 seconds", "one second". For the countdown beside a revealed code. */
export function countdownWords(seconds: number): string {
  const whole = Math.max(0, Math.ceil(seconds));
  if (whole >= 60) return whole === 60 ? 'one minute' : `${Math.ceil(whole / 60)} minutes`;
  if (whole === 1) return 'one second';
  return `${whole} seconds`;
}

/**
 * How long a call lasted, in words: "42 seconds", "4 minutes", "1 hour 5 minutes". Null when the
 * phone system never said, which a screen leaves out rather than writes as zero, because a call
 * of no length and a call nobody measured are different facts.
 */
export function callLengthWords(seconds: number | null): string | null {
  if (seconds === null || seconds < 0) return null;
  if (seconds < 60) return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hourWords = `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  return rest === 0 ? hourWords : `${hourWords} ${rest} ${rest === 1 ? 'minute' : 'minutes'}`;
}

/**
 * "40 minutes late", "about 3 hours late", "2 days late". Null when the server could not say by
 * how much, which a screen leaves out rather than writes as zero.
 */
export function lateByWords(minutes: number | null): string | null {
  if (minutes === null) return null;
  if (minutes < 1) return 'late by less than a minute';
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} late`;
  if (minutes < 60 * 48) {
    const hours = Math.round(minutes / 60);
    return `about ${hours} ${hours === 1 ? 'hour' : 'hours'} late`;
  }
  const days = Math.round(minutes / (60 * 24));
  return `${days} days late`;
}

/* ------------------------------------------------------------------ *
 * Ordering and links that a screen would otherwise get subtly wrong.
 * ------------------------------------------------------------------ */

/**
 * Late invoices first, then by due date, then by when they were made, so the top of the money
 * screen is the thing that most needs a phone call. Undated drafts sort after everything dated.
 * Stable: rows the rule cannot separate keep the order the server gave them.
 */
export function orderInvoices<
  T extends { readonly late: boolean; readonly dueOn: string | null; readonly createdAt: string },
>(rows: readonly T[]): readonly T[] {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      if (a.row.late !== b.row.late) return a.row.late ? -1 : 1;
      if (a.row.dueOn !== b.row.dueOn) {
        if (a.row.dueOn === null) return 1;
        if (b.row.dueOn === null) return -1;
        return a.row.dueOn < b.row.dueOn ? -1 : 1;
      }
      if (a.row.createdAt !== b.row.createdAt) return a.row.createdAt < b.row.createdAt ? -1 : 1;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}

/**
 * A directions link for the address on a sheet. Apple Maps opens on every iPhone and redirects to
 * Google's page on everything else, so one URL serves both without a choice on the screen. The
 * address text is the sheet's own block, lines joined with commas, and nothing is added to it.
 */
export function directionsUrl(addressText: string): string {
  const oneLine = addressText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join(', ');
  return `https://maps.apple.com/?q=${encodeURIComponent(oneLine)}`;
}

/**
 * A SAVED METHOD IN A SENTENCE SHE READS (ops #46, step 3b), after "your": "Visa ending 4242",
 * "Apple Pay Visa ending 4242", "Chase checking account ending 6789", "Link account". Made from the
 * label the office reads it by, which ends in the last four when there are any, and nothing else.
 */
export function savedMethodWords(method: {
  readonly displayLabel: string;
  readonly methodType: SavedMethodType | null;
}): string {
  if (method.methodType === 'link') return 'Link account';
  if (method.methodType === null) return savedMethodNoun(null);
  const label = method.displayLabel.trim();
  const lastFour = /^(.+?) +(\d{4})$/.exec(label);
  const name = lastFour ? lastFour[1]! : label;
  const what =
    method.methodType === 'us_bank_account' && !/\baccount$/i.test(name) ? `${name} account` : name;
  return lastFour ? `${what} ending ${lastFour[2]}` : what;
}

/** What kind of method it is, in a word: "card", "bank account", "Link account". */
export function savedMethodNoun(methodType: SavedMethodType | null): string {
  switch (methodType) {
    case 'card':
      return 'card';
    case 'us_bank_account':
      return 'bank account';
    case 'link':
      return 'Link account';
    default:
      return 'saved payment method';
  }
}

/** Open things before settled ones, keeping the server's order inside each group. */
export function openFirst<T extends { readonly state: string }>(
  rows: readonly T[],
  openState: string,
): readonly T[] {
  return [
    ...rows.filter((row) => row.state === openState),
    ...rows.filter((row) => row.state !== openState),
  ];
}
